import { PropsWithChildren, createContext, useContext, useEffect } from 'react'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { Button, ButtonProps } from 'ui'
import Alert, { alertTypes } from 'ui/Alert'
import { Check, TextArea } from 'ui/fields'

export type CancelForm = {
   reason?: string
   confirmation: boolean
}

type Props = {
   operation: {
      importId: string
   }
   onSubmit: (data: CancelForm) => void
   visible?: boolean
   cancelWarning?: string | null
}
const DeleteOperationFormContext = createContext<Props>({} as Props)

const DeleteOperation = ({ children, visible = true, ...rest }: PropsWithChildren<Props>) => {
   const { reset, ...methodsRest } = useForm<CancelForm>({
      mode: 'onChange',
      defaultValues: {
         confirmation: false,
      },
   })

   useEffect(() => {
      if (!visible) {
         reset()
      }
   }, [visible, reset])

   return (
      <DeleteOperationFormContext.Provider value={{ ...rest }}>
         <FormProvider reset={reset} {...methodsRest}>
            {children}
         </FormProvider>
      </DeleteOperationFormContext.Provider>
   )
}

const Form = () => {
   const intl = useIntl()
   const { onSubmit, operation, cancelWarning } = useContext(DeleteOperationFormContext)
   const {
      handleSubmit,
      register,
      formState: { errors, dirtyFields },
   } = useFormContext<CancelForm>()

   return (
      <form id="cancelationForm" onSubmit={handleSubmit(onSubmit)}>
         <div className="space-y-3 mb-8">
            <p>{intl.formatMessage({ id: 'partner.operation.delete.description' })}</p>
         </div>
         <TextArea
            {...register('reason')}
            label={intl.formatMessage({ id: 'partner.operation.delete.comment' })}
            className="w-full border border-gray-300 rounded p-2 resize-none mb-4"
         />
         <Check
            {...register('confirmation', {
               required: intl.formatMessage({ id: `partner.operation.errors.confirmation` }),
            })}
            label={intl.formatMessage({ id: 'partner.operation.delete.confirmation' }, { id: operation.importId })}
            error={errors?.confirmation?.message}
            isTouched={dirtyFields.confirmation}
         />
         {cancelWarning && (
            <Alert type={alertTypes.GENERIC_WARNING} mode="light" className="text-sm mt-2">
               <Alert.Body>{cancelWarning}</Alert.Body>
            </Alert>
         )}
      </form>
   )
}

const SubmitButton = ({ children, ...rest }: PropsWithChildren<ButtonProps>) => {
   const { handleSubmit } = useFormContext<CancelForm>()
   const { onSubmit } = useContext(DeleteOperationFormContext)
   return (
      <Button {...rest} type="submit" onClick={handleSubmit(onSubmit)}>
         {children}
      </Button>
   )
}

DeleteOperation.Form = Form
DeleteOperation.SubmitButton = SubmitButton

export default DeleteOperation
